import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Table, Card, Row, Col, Alert, Spinner, Modal, ToggleButton, ButtonGroup } from 'react-bootstrap';
import { api } from '../../../shared/api';
import NumberInput from '../../../components/form/NumberInput';
import { formatDateTime } from '../../../shared/utils/dateUtils';
import { IconCalculator, IconPencil, IconRobot, IconEdit } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../shared/CurrencyContext';
import { tariffTypeHumanify } from '../utils/tariffType';
import { useMutation } from '@tanstack/react-query';
import PageSection from '../../../components/layout/PageSection';
import StatsCard from '../../../components/ui/StatsCard';
import EmptyState from '../../../components/feedback/EmptyState';


const RentalSettingsPrices = ({ rental, setRental }) => {
  const { t } = useTranslation();
  const { formatCurrency, currency } = useCurrency();
  const [calculating, setCalculating] = useState(false);
  const [tariffType, setTariffType] = useState('day');
  const [priceBreakdown, setPriceBreakdown] = useState([]);
  const [formData, setFormData] = useState({
    price: 0,
    price_vat: 0,
    discount_percentage: 0
  });
  const [calculatedValues, setCalculatedValues] = useState({
    price: rental.price || 0,
    discount_percentage: rental.discount_percentage || 0,
    price_vat: rental.price || 0,
    original_price: rental.price || 0,
    days: 0
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pricingMode, setPricingMode] = useState(rental.use_ai_pricing ? 'ai' : 'manual');

  const submitMutation = useMutation({
    mutationFn: () => api.patch(`rentals/api/rentals/${rental.id}/`, {
      price: formData.price,
      discount_percentage: formData.discount_percentage,
      use_ai_pricing: pricingMode === 'ai'
    }),
    onSuccess: (response) => {
      setRental(response);
      setSuccess(true);
      setShowEditModal(false);
      window.scrollTo(0, 0);
    },
    onError: () => setError(t('rentals.settings.pricing.updateError')),
  });
  const submitting = submitMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    submitMutation.mutate();
  };

  // Use refs to track rental changes and prevent unnecessary recalculations
  const rentalRef = useRef(rental);
  const previousRentalRef = useRef({
    id: rental.id,
    date_from: rental.date_from,
    date_to: rental.date_to,
    items: rental.rental_items?.length || 0
  });

  // Initialize component when rental changes
  useEffect(() => {

    // Set initial pricing mode based on rental
    setPricingMode(rental.use_ai_pricing ? 'ai' : 'manual');

    // Update refs to current rental
    rentalRef.current = rental;
    previousRentalRef.current = {
      id: rental.id,
      date_from: rental.date_from,
      date_to: rental.date_to,
      items: rental.rental_items?.length || 0
    };
  }, [rental]);

  const handleInputChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const getCurrentPrices = async () => {
     const response = await api.post(`rentals/api/rentals/${rental.id}/calculate_price/`, {
      skip_calculation: true,
      use_ai_pricing: pricingMode === 'ai',
      tariff_type: tariffType
    });

    // Set price breakdown from the API response
    if (response.price_breakdown && response.price_breakdown.length > 0) {
      setPriceBreakdown(response.price_breakdown);
    }

    // Update form and calculated values with current prices
    const newPrice = response.price || 0;
    const newDiscountPercentage = response.discount_percentage || 0;
    const originalPrice = response.original_price || newPrice;
    setFormData({
      price: newPrice,
      price_vat: newPrice,
      discount_percentage: newDiscountPercentage,
      original_price: originalPrice
    });
    setCalculatedValues({
      price: newPrice,
      discount_percentage: newDiscountPercentage,
      price_vat: newPrice,
      original_price: originalPrice,
      days: response.days || calculateDays()
    });
    setRental({
      ...rental,
      price: newPrice,
      discount_percentage: newDiscountPercentage,
      use_ai_pricing: pricingMode === 'ai'
    });
  }


  // Calculate price breakdown with API and save
  const calculatePriceBreakdown = async (useAiPricing = pricingMode === 'ai') => {
    setCalculating(true);
    setError(null);

    try {

      const response = await api.post(`rentals/api/rentals/${rental.id}/calculate_price/`, {
        date_from: rental.date_from,
        date_to: rental.date_to,
        items: rental.rental_items || [],
        use_ai_pricing: useAiPricing,
        save_price: true,
        tariff_type: tariffType
      });


      // Set price breakdown from the API response
      if (response.price_breakdown && response.price_breakdown.length > 0) {
        setPriceBreakdown(response.price_breakdown);
      }

      // Update form and calculated values with calculated prices
      const newPrice = response.price || 0;
      const newDiscountPercentage = response.discount_percentage || 0;
      const originalPrice = response.original_price || newPrice;

      setFormData({
        price: newPrice,
        price_vat: newPrice,
        discount_percentage: newDiscountPercentage,
        original_price: originalPrice
      });

      setCalculatedValues({
        price: newPrice,
        discount_percentage: newDiscountPercentage,
        price_vat: newPrice,
        original_price: originalPrice,
        days: response.days || calculateDays()
      });

      // Update the parent rental object with the new price
      setRental({
        ...rental,
        price: newPrice,
        discount_percentage: newDiscountPercentage,
        use_ai_pricing: useAiPricing
      });

    } catch (error) {
      console.error('Error calculating price breakdown:', error);
      setError(t('rentals.settings.pricing.updateError'));

    } finally {
      setCalculating(false);
    }
  };

  // Handle pricing mode change
  const handlePricingModeChange = async (mode) => {
    setPricingMode(mode);
    // Recalculate prices when switching modes
    await calculatePriceBreakdown(mode === 'ai');
  };

  // Calculate days between dates
  const calculateDays = () => {
    if (!rental.date_from || !rental.date_to) return 0;

    const start = new Date(rental.date_from);
    const end = new Date(rental.date_to);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  // Calculate discount amount
  const calculateDiscount = (price, discountPercentage) => {
    return (price * discountPercentage) / 100;
  };

  return (
    <>
      {success && (
        <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
          {t('rentals.settings.pricing.updateSuccess')}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <PageSection
        title={t('rentals.settings.pricing.title')}
        description={t('rentals.settings.pricing.description', 'Přepínej mezi ručním a AI naceněním, přepočítej cenu a uprav finální částky pro tuto zápůjčku.')}
        actions={
          <>
            <ButtonGroup className="me-2">
              <ToggleButton
                id="pricing-mode-manual"
                type="radio"
                variant={pricingMode === 'manual' ? 'primary' : 'outline-primary'}
                name="pricing-mode"
                value="manual"
                checked={pricingMode === 'manual'}
                onChange={() => handlePricingModeChange('manual')}
              >
                <IconPencil size={16} className="me-2" />
                {t('rentals.settings.pricing.manualMode')}
              </ToggleButton>
              <ToggleButton
                id="pricing-mode-ai"
                type="radio"
                variant={pricingMode === 'ai' ? 'primary' : 'outline-primary'}
                name="pricing-mode"
                value="ai"
                checked={pricingMode === 'ai'}
                onChange={() => handlePricingModeChange('ai')}
              >
                <IconRobot size={16} className="me-2" />
                {t('rentals.settings.pricing.aiMode')}
              </ToggleButton>
            </ButtonGroup>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={() => calculatePriceBreakdown()}
              disabled={calculating}
            >
              <IconCalculator size={16} className="me-2" />
              {calculating ? t('common.saving') : t('rentals.settings.pricing.recalculate')}
            </Button>
            <Button variant="primary" onClick={() => setShowEditModal(true)}>
              <IconEdit size={16} className="me-2" /> {t('common.edit')}
            </Button>
          </>
        }
      >
        <div className="app-stat-grid">
          <StatsCard title={t('rentals.settings.pricing.subtotal')} value={formatCurrency(calculatedValues.price, { decimals: 0 })} icon="receipt-2" colorClass="secondary" />
          <StatsCard title={t('rentals.settings.pricing.total')} value={formatCurrency(calculatedValues.price_vat, { decimals: 0 })} icon="currency-dollar" colorClass="primary" />
          <StatsCard title={t('rentals.settings.pricing.discount')} value={`-${calculatedValues.discount_percentage}%`} icon="percentage" colorClass="danger" />
          <StatsCard title={t('rentals.settings.pricing.totalDays')} value={calculateDays()} icon="calendar-stats" colorClass="warning" />
        </div>

        <div className="app-stat-grid mt-3">
          <StatsCard title={t('rentals.settings.pricing.pricePerDay')} value={formatCurrency(calculateDays() > 0 ? (calculatedValues.price_vat / calculateDays()) : 0, { decimals: 2 })} icon="calendar-time" colorClass="info" />
          <StatsCard title={t('rentals.settings.pricing.includingVat')} value={formatCurrency(calculatedValues.price_vat, { decimals: 0 })} icon="receipt-tax" colorClass="success" />
          <StatsCard title={t('rentals.settings.pricing.discountAmount', 'Sleva v částce')} value={`-${formatCurrency(calculateDiscount(calculatedValues.price, calculatedValues.discount_percentage), { decimals: 0 })}`} icon="discount-2" colorClass="danger" />
        </div>
      </PageSection>

      {calculating ? (
        <PageSection>
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2 mb-0">{t('rentals.settings.pricing.calculating')}</p>
          </div>
        </PageSection>
      ) : priceBreakdown.length > 0 ? (
        <PageSection
          title={t('rentals.settings.pricing.breakdown')}
          description={t('rentals.settings.pricing.breakdownDescription', 'Rozpis jednotlivých položek, jednotek a výsledných mezisoučtů po přepočtu.')}
        >
            <Table responsive className="table-vcenter app-table mb-0">
              <thead>
                <tr>
                  <th>{t('rentals.settings.items.item')}</th>
                  <th>{t('rentals.settings.items.quantity')}</th>
                  <th>{t('rentals.settings.pricing.units')}</th>
                  <th>{t('rentals.settings.pricing.unitType')}</th>
                  <th>{t('rentals.settings.pricing.pricePerUnit')}</th>
                  <th>{t('rentals.settings.pricing.subtotal')}</th>
                </tr>
              </thead>
              <tbody>
                {priceBreakdown.map(item => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>{item.units_count || 0}</td>
                    <td>{tariffTypeHumanify(item.tariff_type || 'day', t)}</td>
                    <td>{formatCurrency(item.tariff || 0, { decimals: 2 })}</td>
                    <td>{formatCurrency(item.total || 0, { decimals: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
        </PageSection>
      ) : (
        <PageSection>
          <EmptyState
            title={t('rentals.settings.pricing.noBreakdownTitle', 'Rozpis ceny zatím není k dispozici')}
            message={t('rentals.settings.pricing.noBreakdownMessage', 'Přepočítej cenu a systém připraví rozpad po položkách a tarifech.')}
            action={
              <Button variant="primary" onClick={() => calculatePriceBreakdown()} disabled={calculating}>
                <IconCalculator size={16} className="me-2" />
                {t('rentals.settings.pricing.recalculate')}
              </Button>
            }
          />
        </PageSection>
      )}

      {/* Edit Price Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('rentals.settings.pricing.editTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.pricing.subtotal')} ({currency})</Form.Label>
              <NumberInput
                step="any"
                min={0}
                value={formData.price}
                onChange={(value) => handleInputChange('price', value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.pricing.discount')} (%)</Form.Label>
              <NumberInput
                step="any"
                min={0}
                max={100}
                value={formData.discount_percentage}
                onChange={(value) => handleInputChange('discount_percentage', value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.pricing.totalWithTax')} ({currency})</Form.Label>
              <NumberInput
                step="any"
                min={0}
                value={formData.price_vat}
                onChange={(value) => handleInputChange('price_vat', value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('common.saving')}
              </>
            ) : (
              t('common.save')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RentalSettingsPrices;
